import { Injectable } from "@angular/core";

import { CryptoFunctionService } from "@bitwarden/common/abstractions/cryptoFunction.service";
import { EncryptionType } from "@bitwarden/common/enums/encryptionType";
import { EncString } from "@bitwarden/common/models/domain/enc-string";
import { SymmetricCryptoKey } from "@bitwarden/common/models/domain/symmetric-crypto-key";
import { Utils } from "@bitwarden/common/misc/utils";

import { DeviceKeyStorageService } from "./device-key-storage.service";
import { DeviceKeyPair } from "./device-trust.types";

const RSA_OAEP_PARAMS: RsaHashedKeyGenParams = {
  name: "RSA-OAEP",
  modulusLength: 2048,
  publicExponent: new Uint8Array([0x01, 0x00, 0x01]),
  hash: "SHA-256",
};
const RSA_OAEP_IMPORT_PARAMS: RsaHashedImportParams = { name: "RSA-OAEP", hash: "SHA-256" };

/**
 * Trusted Device Encryption (TDE) crypto primitives — Phase 1 of GOOGLE_SSO_PLAN.md's TDE
 * checklist: device keypair generation/storage + UserKey generation + wrap/unwrap only.
 * No backend calls of its own — `FirstDeviceSetupService`, `ReturningDeviceUnlockService` and
 * `DeviceApprovalService` build the flows on top of these primitives.
 */
@Injectable({
  providedIn: "root",
})
export class DeviceTrustService {
  constructor(
    private cryptoFunctionService: CryptoFunctionService,
    private deviceKeyStorage: DeviceKeyStorageService,
  ) {}

  /**
   * Returns this browser's stored device keypair, or null if none exists (never generated,
   * or IndexedDB was cleared). Never generates one — use this wherever an existing,
   * server-registered keypair is required.
   */
  async getDeviceKeyPair(): Promise<DeviceKeyPair | null> {
    const stored = await this.deviceKeyStorage.get();
    if (stored == null) {
      return null;
    }
    return {
      publicKey: await this.importPublicKey(stored.publicKeySpki),
      privateKey: stored.privateKey,
      publicKeySpki: stored.publicKeySpki,
    };
  }

  /**
   * Returns this browser's device keypair, generating and persisting one on first use.
   * Safe to call repeatedly — subsequent calls return the same stored keypair.
   */
  async getOrCreateDeviceKeyPair(): Promise<DeviceKeyPair> {
    return (await this.getDeviceKeyPair()) ?? this.generateAndStoreDeviceKeyPair();
  }

  /**
   * Records that this device unlocked `userId`'s vault with its own key, so it can approve
   * that account's new devices. A no-op when there is no stored keypair or it is already
   * marked for `userId`.
   */
  async markTrustedFor(userId: string): Promise<void> {
    const stored = await this.deviceKeyStorage.get();
    if (stored == null || stored.trustedForUserId === userId) {
      return;
    }
    await this.deviceKeyStorage.put({ ...stored, trustedForUserId: userId });
  }

  /** Whether `markTrustedFor(userId)` has been recorded for this device's current keypair. */
  async isTrustedFor(userId: string): Promise<boolean> {
    const stored = await this.deviceKeyStorage.get();
    return userId != null && stored?.trustedForUserId === userId;
  }

  /** Generates a brand-new random UserKey (64 bytes: AES-256 key + HMAC-SHA256 key). */
  async generateUserKey(): Promise<SymmetricCryptoKey> {
    const keyBytes = await this.cryptoFunctionService.randomBytes(64);
    return new SymmetricCryptoKey(keyBytes);
  }

  /** Wraps (encrypts) a UserKey under a device's RSA public key. */
  async wrapUserKey(userKey: SymmetricCryptoKey, publicKey: CryptoKey): Promise<EncString> {
    const ciphertext = await crypto.subtle.encrypt({ name: "RSA-OAEP" }, publicKey, userKey.key);
    return new EncString(EncryptionType.Rsa2048_OaepSha256_B64, Utils.fromBufferToB64(ciphertext));
  }

  /** Unwraps (decrypts) a UserKey previously wrapped under this device's public key. */
  async unwrapUserKey(wrapped: EncString, privateKey: CryptoKey): Promise<SymmetricCryptoKey> {
    if (wrapped.encryptionType !== EncryptionType.Rsa2048_OaepSha256_B64) {
      throw new Error("Unsupported wrapped UserKey encryption type");
    }

    const plaintext = await crypto.subtle.decrypt(
      { name: "RSA-OAEP" },
      privateKey,
      wrapped.dataBytes,
    );
    return new SymmetricCryptoKey(plaintext);
  }

  private async generateAndStoreDeviceKeyPair(): Promise<DeviceKeyPair> {
    // Web Crypto has no way to generate a keypair with per-key extractability, so the
    // private key is generated extractable, exported once, then immediately re-imported
    // as non-extractable before anything is persisted. The extractable CryptoKey and the
    // raw pkcs8 buffer are never stored and fall out of scope right after — only the
    // non-extractable key ever reaches IndexedDB.
    const keyPair = await crypto.subtle.generateKey(RSA_OAEP_PARAMS, true, ["encrypt", "decrypt"]);
    const publicKeySpki = await crypto.subtle.exportKey("spki", keyPair.publicKey);
    const pkcs8 = await crypto.subtle.exportKey("pkcs8", keyPair.privateKey);

    const nonExtractablePrivateKey = await crypto.subtle.importKey(
      "pkcs8",
      pkcs8,
      RSA_OAEP_IMPORT_PARAMS,
      false,
      ["decrypt"],
    );

    await this.deviceKeyStorage.put({
      publicKeySpki,
      privateKey: nonExtractablePrivateKey,
    });

    const publicKey = await this.importPublicKey(publicKeySpki);
    return { publicKey, privateKey: nonExtractablePrivateKey, publicKeySpki };
  }

  /** Imports a raw SPKI public key (this device's own, or another device's being approved). */
  importPublicKey(spki: ArrayBuffer): Promise<CryptoKey> {
    return crypto.subtle.importKey("spki", spki, RSA_OAEP_IMPORT_PARAMS, true, ["encrypt"]);
  }
}
